import { has, get, some, clone, cloneDeep, findIndex, includes, isEmpty } from "lodash";
import serverApi from '../lib/serverApi';
import attestivNet from '../lib/AttestivNet';
import cookieHelper from '../helpers/cookies';
import appCfg from '../config';
import strings from "../resources/strings";
import generate from 'generate-password';
import AttestivNet from "../lib/AttestivNet";
import { getQuestionAnalysis } from "../helpers/analysis";

var state = {};
var addlQuestionTemplate = {
    questionID: undefined,
    questionType: appCfg.additionalQuestions.questionType,
    questionSpecifier: "additionalQuestion",
    analysis: [],
    maxRetryRequests: 0,
    questionLabel: undefined,
    cameraVisible: false,
    onCapture: { pictureID: undefined, cameraFields: {} },
    options: [{
        itemID: "option-0",
        label: strings.getString(appCfg.additionalQuestions.questionType === "allCameras" ? "takeAPictureOrVideo" :
                                    (appCfg.additionalQuestions.questionType === "camera" ? "takeAPicture" : "recordAVideo")),
        action: "SHOW_CAMERA",
        actionState: {}
    }],
    answerType: "JPEG_URL",
    answerValue: ""
};

const initializeState = state => {
    return {
        ...state,
        isLoading: false,
        currentQuestionRetries: 0,
        canAdvance: true,
        questionHistory: [],
        currentAnswerIndex: -1,
        resumeMsgVisible: false,
        fileUploadsInProgress: 0,
        failedFileUploads: 0,
        backgroundRequestsInProgress: 0,
        maxBackgroundRequestsInProgress: 0,
        submittedWorkflow: false,
        logs: [],
        surveyCode: generate.generate({length: 10, numbers: true, uppercase: false}) // used for displaying a survey code at end when collecting mturk data
    };
};

const currentQuestion = state => {
    let currentQuestionID = has(state, "workflowSession") ? state.workflowSession.currentQuestionId : undefined;
    if (currentQuestionID) {
        for (let q of state.questionSet.questionSet.questions) {
            if (q.questionID === currentQuestionID) {
                return q;
            }
        }
    }
    return null;
};

const currentAnswer = state => {
    return state.questionHistory[state.currentAnswerIndex];
};

const assembleAllQuestions = state => {
    let questionSet = state.questionSet.questionSet;
    let clientRequest = state.clientRequest;

    if (!clientRequest || !("additionalQuestions" in clientRequest) || clientRequest.additionalQuestions.length === 0) {
        return { ...state };
    }

    let defaultQuestionOrder = questionSet.defaultQuestionOrder;
    let additionalQuestionInsertionIndex =
        "additionalQuestionInsertionIndex" in questionSet
            ? questionSet.additionalQuestionInsertionIndex
            : defaultQuestionOrder.length - 2; // -2 assumes confirmation and completion steps

    let questionOrder = defaultQuestionOrder.slice(0, additionalQuestionInsertionIndex);
    let mergedQuestions = questionSet.questions.slice(0, additionalQuestionInsertionIndex);

    for (let i = 1; i <= clientRequest.additionalQuestions.length; i++) {

        let addlq = cloneDeep(addlQuestionTemplate);
        addlq.questionID = get(clientRequest.additionalQuestions[i - 1], "questionID", "additional-question-" + i);
        questionOrder.push(get(clientRequest.additionalQuestions[i - 1], "questionID", "additional-question-" + i));
        addlq.onCapture.pictureID = get(clientRequest.additionalQuestions[i - 1], ["onCapture", "pictureID"], "additionalQuestion_" + i);
        addlq.questionLabel = clientRequest.additionalQuestions[i - 1].question;
        
        if (!isEmpty(get(clientRequest.additionalQuestions[i - 1], "fields"))) {
            addlq.fields = clientRequest.additionalQuestions[i - 1].fields;
        }

        if (!isEmpty(clientRequest.additionalQuestions[i - 1].onCapture)){
            addlq.onCapture = clientRequest.additionalQuestions[i - 1].onCapture;
        }

        if (!isEmpty(clientRequest.additionalQuestions[i - 1].questionType)) {
            addlq.questionType = clientRequest.additionalQuestions[i - 1].questionType
            addlq.options = [];
        } else if (has(state.questionSet, "additionalQuestions.questionType")) {
            addlq.questionType = get(state.questionSet, "additionalQuestions.questionType");
            addlq.options[0].label = "Take a " + (addlq.questionType === "allCameras" ? "Picture/Video" :
                                    (addlq.questionType === "camera" ? "Picture" : "Video"));
        }

        // If analysis type has been specified, add it to the question
        let analysis = get(clientRequest, ["additionalQuestions", `${i-1}`, "analysis"], null)
        if (analysis){
            addlq.analysis = [...addlq.analysis, ...analysis]
        }

        // add any analysis if needed
        if (!AttestivNet.isFacialDetectionQuestion(addlq) && !AttestivNet.isLicensePictureQuestion(addlq)) {
            if (!includes(addlq.analysis, "triageFull") && (appCfg.additionalQuestions.enableTriage || get(state.questionSet, "additionalQuestions.enableTriage", false))) {
                addlq.analysis.push("triageFull");
            }
            if (!includes(addlq.analysis, "objectDetection") && (appCfg.additionalQuestions.enableObjectDetection || get(state.questionSet, "additionalQuestions.enableObjectDetection", false))) {
                addlq.analysis.push("objectDetection");
            }
        }
        mergedQuestions.push(addlq);
    }

    let remainingOrder = defaultQuestionOrder.slice(additionalQuestionInsertionIndex);
    if (remainingOrder && remainingOrder.length > 0) {
        questionOrder = questionOrder.concat(remainingOrder);
    }
    let remainingQuestions = questionSet.questions.slice(additionalQuestionInsertionIndex);
    if (remainingQuestions && remainingQuestions.length > 0) {
        mergedQuestions = mergedQuestions.concat(remainingQuestions);
    }

    console.debug("Merged additional questions");
    return {
        ...state,
        questionSet: {
            ...state.questionSet,
            questionSet: {
                ...state.questionSet.questionSet,
                defaultQuestionOrder: questionOrder,
                questions: mergedQuestions
            }
        }
    };
};

const nextQuestionStep = (state, question, action) => {
    let questionOrder = state.questionSet.questionSet.defaultQuestionOrder;

    // allow the question to override the ordering
    if (action.hasOwnProperty("selectedOption") && action.selectedOption.hasOwnProperty("nextQuestion")) {
        return action.selectedOption.nextQuestion;
    }
    else if (question.hasOwnProperty("nextQuestionMap")) {
        let questionID = question.nextQuestionMap.questionID;
        let fieldID = question.nextQuestionMap.fieldID;
        let answer = findAnswer(state, questionID, fieldID);
        if (answer) {
            let value = answer.value;
            return question.nextQuestionMap.values[value];
        }
    }
    else if (action.type === "NEXT_QUESTION" && action.nextQuestion && action.nextQuestion.trim().length > 0) {
        return action.nextQuestion;
    }
    else if (question.hasOwnProperty("nextQuestion")) {
        let nextIndex = questionOrder.indexOf(question.nextQuestion);
        if (nextIndex >= 0) {
            return questionOrder[nextIndex];
        }
        else {
            return question.nextQuestion
        }
    }

    let current = -1;

    // find the next question in the default ordering
    if (question) {
        current = questionOrder.indexOf(question.questionID);
    }

    if (current < 0 || current === questionOrder.length) {
        return "";
    } else {
        return questionOrder[current + 1];
    }
};

const isQuestionComplete = (question, answer, action, state) => {
    if (question && question.questionType === "form") {
        if (action && has(action, "selectedOption")
            && has(action.selectedOption, "ignoreRequiredFields")
            && action.selectedOption.ignoreRequiredFields === true)
        {
            return true;
        }

        const hasArray = some(question.fields, field => field.fieldType === 'array');
        if (!hasArray && (question.fields.length !== answer.fields.length
            || question.questionID !== answer.questionID))
        {
            return false;
        }

        for (let i = 0; i < answer.fields.length; i++) {
            if (question.fields[i] && question.fields[i].required
                    && question.fields[i].fieldID === answer.fields[i].fieldID
                    && answer.fields[i].value.trim() === "")
            {
                return false;
            }
        }

        let hasFormatErrors = "valueFormatErrors" in state && state.valueFormatErrors && state.valueFormatErrors.length > 0;
        if (hasFormatErrors) {
            return false;
        }
    }

    return true;
};

const uploadAnswerData = (answer, state) => {
    console.debug("*** UPLOADING ANSWER DATA");
    serverApi.ensureRecord(state).then(function(recordId) {
        if (!recordId) {
            throw new Error("Did not get recordId back from ensureRecord");
        }
        serverApi.uploadWorkflowData(recordId, answer, state).then(function() {
            if (has(answer, "file") || has(answer, "tamperedMedia")) {
                console.debug(`uploadAnswerData analysis: ${answer.analysis}`);
                serverApi.uploadMedia(recordId, answer, state);
            }
            serverApi.uploadBillableOperationsData(state);
        });
    });
}


const initiateUploadAnswerData = (state, action) => {
    console.debug("*** INITIATE UPLOAD ANSWER DATA");
    let currAnswer = state.questionHistory[action.answerIndex];

    let answer = cloneDeep(currAnswer);
    answer.filesUploaded = true;

    let questionHistory = clone(state.questionHistory);
    questionHistory[action.answerIndex] = answer;    

    uploadAnswerData(currAnswer, state);
    return {
        ...state,
        questionHistory: questionHistory
    };
}

const nextQuestion = (state, action) => {
    const question = currentQuestion(state);
    const answer = currentAnswer(state);
    let prevAnswerIndex = state.currentAnswerIndex - 1 < 0 ? 0 : state.currentAnswerIndex - 1;
    const prevAnswer = state.questionHistory[prevAnswerIndex];
    console.log("questionID: " + question.questionID);

    if (isQuestionComplete(question, answer, action, state)) {
        // Reaching the submit screen means all answer uploads have already been
        // initiated. The NEXT_QUESTION dispatched after committing the record
        // must not treat the last answer as pending and upload it a second time.
        if (!state.submittedWorkflow && question.questionType !== "submit") {
            // if no background models then just start the data upload otherwise a different
            // dispatched event will start the start the upload after any background tasks
            // like calling triage or object detection have finished
            if (prevAnswer
                && !prevAnswer.backgroundMediaFuncs
                && !prevAnswer.filesUploaded)
            {
                console.debug("*** NEXT QUESTION");
                uploadAnswerData(prevAnswer, state);
            }
            else if (answer && answer.fields.length > 0) {
                console.debug("*** NEXT QUESTION");
                uploadAnswerData(answer, state);
            }
        }
        return nextQuestionStep(state, question, action);
    }

    return "";
};

const canAdvance = (state, action) => {
    if (!state.canAdvance) {
        return false
    }

    const current = currentQuestion(state);
    const answer = currentAnswer(state);

    if (!current || current.questionType === "complete") {
        return false;
    }

    return isQuestionComplete(current, answer, action, state);
};

const updateRetries = (state, count) => {
    return {
        ...state,
        currentQuestionRetries: count
    };
};

const submittedWorkflow = (state) => {
    return {
        ...state,
        submittedWorkflow: true
    };
};

const createAnswerObject = (question, state) => {
    let forwardAnswers = has(state, "forwardAnswers") ? state.forwardAnswers : {};
    if (forwardAnswers[question.questionID]) {
        let forwardAnswer = forwardAnswers[question.questionID];
        delete forwardAnswers[question.questionID];
        return forwardAnswer;
    }

    let answer = {
        questionID: question.questionID,
        questionType: question.questionType,
        fields: has(question, "fields") ? cloneDeep(question.fields) : [],
        analysis: getQuestionAnalysis(question),
        triageType: get(question, "triageType"),
        models: get(question, "models"),
        filesUploaded: false
    };

    console.debug(`${question.questionID} analysis: ${answer.analysis}`);
    return answer;
};

const constructRecordNameFromFormData = (state) => {
    let clientRequest = state.clientRequest;
    let questionSet = state.questionSet;
    let name = "", recordNameProps;

    // if there is no client request, then try to update the name from the form answer data
    if (!clientRequest &&
        "recordNameProperties" in questionSet &&
        questionSet.recordNameProperties &&
        questionSet.recordNameProperties.length > 0
    ) {
        recordNameProps = questionSet.recordNameProperties;
        for (let i = 0; i < recordNameProps.length; i++) {
            let parts = recordNameProps[i].split(".");
            let q = state.questionHistory.filter(ans => ans.questionID === parts[0]);
            if (q && q.length > 0 && q[0].fields) {
                let f = q[0].fields.filter(field => field.fieldID === parts[1]);
                if (f && f.length > 0 && f[0].value !== undefined && f[0].value !== null && f[0].value !== "") {
                    name += f[0].value;
                    if (i < recordNameProps.length - 1) {
                        name += " ";
                    }
                }
            }
        }

        return name.trim() || null;
    }

    return null;
};

const advanceQuestion = (state, action) => {
    if (!canAdvance(state, action)) {
        return state;
    }

    const current = currentQuestion(state);
    var answer = currentAnswer(state);

    console.debug("Current question: " + current.questionID);
    console.debug("Current answer: " + answer.questionID);

    let isRetakePhoto = get(action, "selectedOption.actionState.retakePhoto", false);
    let questionHistoryRemoved, removalCount;
    let forwardAnswers = has(state, "forwardAnswers") ? cloneDeep(state.forwardAnswers) : {};
    if (isRetakePhoto) {
        // retake photo logic, remove mediaMetadata and workflowData entries
        // for photos which will be replaced

        //nextQuestionId = nextQuestionStep(state, current, action);

        removalCount = get(action, "selectedOption.actionState.retakePhotoRemovalCount", 2);
        let qh, qIdsToRemove = [], uuidsToRemove = [];
        const urlPrefillFields = get(state, "urlPrefillFields", {});
        const extractedFieldsByQuestion = {};

        for (let i = 1; i <= removalCount; i++) {
            qh = state.questionHistory[state.questionHistory.length - i];
            const q = findQuestion(state, qh.questionID);
            const cameraFields = get(q, "onCapture.cameraFields", {});
            Object.values(cameraFields).forEach((path) => {
                const parts = path.split(".");
                if (parts.length < 2) {
                    return;
                }
                const targetQuestionId = parts[0];
                const fieldId = parts[1];
                if (!extractedFieldsByQuestion[targetQuestionId]) {
                    extractedFieldsByQuestion[targetQuestionId] = new Set();
                }
                extractedFieldsByQuestion[targetQuestionId].add(fieldId);
            });
        }

        for (let i = 1; i <= removalCount; i++) {
            qh = state.questionHistory[state.questionHistory.length - i];
            const fieldsToClear = extractedFieldsByQuestion[qh.questionID];
            if (fieldsToClear) {
                const preserved = cloneDeep(qh);
                if (Array.isArray(preserved.fields)) {
                    preserved.fields = preserved.fields.map((field) => {
                        if (!fieldsToClear.has(field.fieldID)) {
                            return field;
                        }
                        if ((urlPrefillFields[qh.questionID] || []).includes(field.fieldID)) {
                            return field;
                        }
                        return {
                            ...field,
                            value: "",
                            signedValue: undefined,
                            signature: undefined
                        };
                    });
                }
                forwardAnswers[qh.questionID] = preserved;
            }
        }
        for (let i = 1; i <= removalCount; i++) {
            qh = state.questionHistory[state.questionHistory.length - i];
            if (has(qh, "mediaJSON")) {
                uuidsToRemove.push(qh.mediaJSON.uuid);
            }
            qIdsToRemove.push(qh.questionID);
        }

        questionHistoryRemoved = state.questionHistory.slice(0, -removalCount); // yes, negative removalCount

        if (uuidsToRemove.length > 0) {
            attestivNet.removeMediaMetadata(uuidsToRemove, state)
                .then((response) => {
                    console.log(response);
                })
                .catch((error) => {
                    console.error(error);
                });
        }

        if (qIdsToRemove.length > 0) {
            attestivNet.removeWorkflowData(qIdsToRemove, state)
                .then((response) => {
                    console.log(response);
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }

    let nextQuestionId = nextQuestion(state, action);
    const nextQ = findQuestion(state, nextQuestionId);
    var nextAnswer = createAnswerObject(nextQ, state);

    if (current.questionID !== "confirmation") {
        console.debug("Updating question id in workflow session to " + nextQuestionId);
        attestivNet.updateWorkflowSession(state, nextQuestionId)
            .then((response) => {
                console.log(response);
                if ("status" in response && response.status === "Failure") {
                    console.error(response.error);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }
    else {
        cookieHelper.removeCookie(state.referenceId);
    }

    let newState = {
        ...state,
        currentQuestionRetries: 0,
        currentAnswerIndex: isRetakePhoto ? questionHistoryRemoved.length : state.questionHistory.length,
        questionHistory: isRetakePhoto ? questionHistoryRemoved.concat([nextAnswer]) : state.questionHistory.concat([nextAnswer]),
        workflowSession: {
            ...state.workflowSession,
            currentQuestionId: nextQuestionId
        }
    };

    if (isRetakePhoto) {
        newState.forwardAnswers = forwardAnswers;
    }

    if (nextQuestionId === "completion") {
        console.debug("Completing and logging out");
        // Sticky requests can be used repeatedly
        // We don't want to spam users with completion emails
        if (state.clientRequest && state.clientRequest.state !== "STICKY") {
            console.debug("Sending completion email for client request");
            attestivNet.updateWorkflowSession(state, null, null, null, true).finally(() => {
                attestivNet.sendRequestCompletionEmail(state.clientRequest, state.questionSet, constructRecordNameFromFormData(state)).finally(() => {
                    attestivNet.markAsCompleted(state.requestId, state.recordId);
                });
            });
        } else if (state.questionSet) {
            console.debug("Sending completion email for workflow");
            attestivNet.updateWorkflowSession(state, null, null, null, true).finally(() => {
                attestivNet.sendRequestCompletionEmail(undefined, { referenceId: state.referenceId, organization: state.organization }, constructRecordNameFromFormData(state));
            });
        } else if (!state.clientRequest && !state.questionSet) {
            console.debug("Sending completion email for unsolicited submission");
            attestivNet.updateWorkflowSession(state, null, null, null, true).finally(() => {
                attestivNet.sendRequestCompletionEmail(undefined, { organization: state.organization }, constructRecordNameFromFormData(state))
            });
        }
        newState.isLoading = false;
        newState.loadingText = "";
        newState.submittedWorkflow = true;
        logout(state);
    }

    return newState;
};

const logout = (state) => {
    attestivNet.logout()
        .then(function(response) {
            console.debug(response);
            console.debug(response.status === "Success" ? "Logout Successful" : JSON.stringify(response));
        })
        .catch(function(error) {
            console.error(error);
        });
}

const previousQuestion = state => {
    if (state.questionHistory.length <= 0) {
        return state;
    }
    else {
        return {
            ...state,
            currentAnswerIndex: state.currentAnswerIndex--,
            workflowSession: {
                ...state.workflowSession,
                currentQuestionId: state.questionHistory[state.currentAnswerIndex].questionID
            }
        };
    }
};

const setCanAdvance = (state, canAdvance) => {
    return {
        ...state,
        canAdvance: canAdvance
    }
}

const findQuestion = (state, questionName) => {
    let questions = state.questionSet.questionSet.questions.filter(q => q.questionID === questionName);
    if (questions.length < 0) {
        return null;
    }
    return questions[0];
};

const findAnswer = function(state, questionID, fieldID) {
    let questionHistory = state.questionHistory;
    let qh, answer;

    for (let i = questionHistory.length - 1; i >= 0; i--) {
        if (questionHistory[i].questionID === questionID) {
            qh = questionHistory[i];
            break;
        }
    }

    if (!qh || !fieldID) {
        return undefined;
    }

    for (let i = 0; i < qh.fields.length; i++) {
        if (qh.fields[i].fieldID === fieldID) {
            answer = qh.fields[i];
            break;
        }
    }

    if (!answer) {
        return undefined;
    }

    let result = {value: answer.value};
    if ("confidence" in answer) {
        result.confidence = answer.confidence;
    }

    return result;
};

const startLoading = (state, action) => {
    return {
        ...state,
        isLoading: true,
        loadingText: action && "loadingText" in action && action.loadingText ? action.loadingText : ""
    };
};

const stopLoading = state => {
    return {
        ...state,
        isLoading: false,
        loadingText: ""
    };
};

const incrementFileUploadCounter = (state) => {
    console.debug("Incrementing file upload counter to " + (state.fileUploadsInProgress + 1));

    return {
        ...state,
        fileUploadsInProgress: state.fileUploadsInProgress + 1
    }
};

const decrementFileUploadCounter = (state, action) => {
    console.debug("Decrementing file upload counter to " + (state.fileUploadsInProgress - 1));

    return {
        ...state,
        fileUploadsInProgress: state.fileUploadsInProgress - 1 <= 0 ? 0 : state.fileUploadsInProgress - 1,
        failedFileUploads: action.success ? state.failedFileUploads : state.failedFileUploads + 1
    }
};

const incrementBackgroundRequestCounter = (state) => {
    console.debug("Incrementing background request counter to " + (state.backgroundRequestsInProgress + 1));

    return {
        ...state,
        backgroundRequestsInProgress: state.backgroundRequestsInProgress + 1,
        maxBackgroundRequestsInProgress: state.backgroundRequestsInProgress + 1 > state.maxBackgroundRequestsInProgress
                ? state.maxBackgroundRequestsInProgress + 1 : state.maxBackgroundRequestsInProgress
    }
};

const decrementBackgroundRequestCounter = (state) => {
    console.debug("Decrementing background request counter to " + (state.backgroundRequestsInProgress - 1));

    return {
        ...state,
        backgroundRequestsInProgress: state.backgroundRequestsInProgress - 1 <= 0 ? 0 : state.backgroundRequestsInProgress - 1
    }
};

const addBackgroundMediaFuncs = (state, action) => {
    let answer = cloneDeep(state.questionHistory[action.answerIndex]);
    answer.backgroundMediaFuncs = action.backgroundMediaFuncs;

    let questionHistory = clone(state.questionHistory);
    questionHistory[action.answerIndex] = answer;    

    return {
        ...state,
        questionHistory: questionHistory
    };
};

const removeBackgroundMediaFuncs = (state, action) => {
    let answer = cloneDeep(state.questionHistory[action.answerIndex]);
    delete answer.backgroundMediaFuncs;

    let questionHistory = clone(state.questionHistory);
    questionHistory[action.answerIndex] = answer;    

    return {
        ...state,
        questionHistory: questionHistory
    };
};

const updateFileUploadProgress = (state, action) => {
    return {
        ...state,
        loadingText: "loadingText" in action && action.loadingText ? action.loadingText : "",
        loadingPercentage: "loadingPercentage" in action && action.loadingPercentage ? action.loadingPercentage : 0,
        fileLoadingPercentage:
            "fileLoadingPercentage" in action && action.fileLoadingPercentage ? action.fileLoadingPercentage : 0
    };
};

const saveLocationData = (state, action) => {
    return {
        ...state,
        location: action.location
    };
};

const valueFormatError = (state, action) => {
    let valueFormatErrors = clone(state.valueFormatErrors);
    if (!action.valueFormatError) { // remove the element if action.valueFormatError is false
        if (!valueFormatErrors) { // if list is empty, then do nothing
            return {...state};
        }

        let index = findIndex(valueFormatErrors, o => o.fieldID === action.fieldID);
        if (index === -1) {
            return {...state};
        }

        valueFormatErrors.splice(index, 1);
        return {
            ...state,
            valueFormatErrors: valueFormatErrors
        };
    }

    let index = findIndex(valueFormatErrors, o => o.fieldID === action.fieldID);
    if (index !== -1) {
        return {...state};
    }

    let obj = {fieldID: action.fieldID, valueFormatError: action.valueFormatError};
    if (!("valueFormatErrors" in state)) {
        return {
            ...state,
            valueFormatErrors: [obj]
        };
    }

    index = findIndex(valueFormatErrors, o => o.fieldID === action.fieldID);
    valueFormatErrors ? valueFormatErrors.push(obj) : valueFormatErrors = [obj];
    return {
        ...state,
        valueFormatErrors: valueFormatErrors
    };
};

const displayUserMessage = (state, action) => {
    let newState = {
        ...state,
        messageAreaVisible: true,
        messageAreaTextColor: get(action, "textColor", "green"),
        messageAreaBackgroundColor: get(action, "backgroundColor", "white"),
        messageAreaText: action.message
    };

    return newState;
};

const hideUserMessage = (state) => {
    return {
        ...state,
        messageAreaVisible: false,
        messageAreaTextColor: "green",
        messageAreaBackgroundColor: "white",
        messageAreaText: ""
    };
};

export {
    state,
    initializeState,
    currentQuestion,
    currentAnswer,
    isQuestionComplete,
    canAdvance,
    updateRetries,
    createAnswerObject,
    advanceQuestion,
    previousQuestion,
    setCanAdvance,
    findQuestion,
    findAnswer,
    assembleAllQuestions,
    saveLocationData,
    valueFormatError,
    initiateUploadAnswerData,
    startLoading,
    stopLoading,
    updateFileUploadProgress,
    addBackgroundMediaFuncs,
    removeBackgroundMediaFuncs,
    submittedWorkflow,
    displayUserMessage,
    hideUserMessage,
    incrementFileUploadCounter,
    decrementFileUploadCounter,
    incrementBackgroundRequestCounter,
    decrementBackgroundRequestCounter
};
